import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ChevronDown, ArrowDownLeft, FileDown, FileText, Wallet, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Panel, GainBadge, Skeleton, EmptyState } from "../components/ui";
import { BuyDialog, SellDialog } from "../components/Dialogs";
import InstrumentLogo from "../components/InstrumentLogo";
import TaxImpactPanel from "../components/TaxImpactPanel";
import TransactionsPanel from "../components/TransactionsPanel";
import { api, downloadExport } from "../lib/api";
import { num, shortDate, gainColor, pct, money, isCryptoType } from "../lib/format";
import { useMoney } from "../context/MoneyContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import useRefreshOnReturn from "../lib/useRefreshOnReturn";

// Bought date, broker, quantity, price, fee, value, P/L — each gets its own
// column instead of quantity/price/fee being crammed into one cell.
const LOT_GRID = "grid-cols-[90px_100px_90px_100px_74px_120px_1fr]";

export default function Holdings() {
  const { t } = useTranslation();
  const { display } = useMoney();
  const [holdings, setHoldings] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [sell, setSell] = useState({ open: false, ticker: "" });

  // `silent` skips the skeleton so a background refresh updates values in place.
  const load = (silent) => {
    if (!silent) setLoading(true);
    api.holdings().then((r) => setHoldings(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  const loadPolicies = () => api.insurancePolicies().then((r) => setPolicies(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { loadPolicies(); }, []);
  useRefreshOnReturn(() => { load(true); loadPolicies(); });

  // Summed per currency — kept separate from the tradable holdings above, since
  // these are insurance-wrapped positions with different liquidity/tax rules.
  const policyTotalsByCcy = policies.reduce((acc, p) => {
    const ccy = p.currency || "EUR";
    acc[ccy] = (acc[ccy] || 0) + (p.projection?.current_value || 0);
    return acc;
  }, {});

  const doExport = async (fmt) => {
    try { await downloadExport("holdings", fmt); toast.success(`Exported ${fmt.toUpperCase()}`); }
    catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="label">{t("holdings.activePositions")}</span>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">{t("holdings.title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-holdings-csv" className="btn-outline" onClick={() => doExport("csv")}><FileDown size={16} /> CSV</button>
          <button data-testid="export-holdings-pdf" className="btn-outline" onClick={() => doExport("pdf")}><FileText size={16} /> PDF</button>
          <button data-testid="holdings-sell-btn" className="btn-outline" onClick={() => setSell({ open: true, ticker: "" })}><ArrowDownLeft size={18} /> {t("common.sell")}</button>
          <button data-testid="holdings-buy-btn" className="btn-primary" onClick={() => setBuyOpen(true)}><Plus size={18} /> {t("common.buy")}</button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : holdings.length === 0 ? (
        <EmptyState testid="holdings-empty" title={t("holdings.emptyTitle")} description={t("holdings.emptyDesc")}
          action={<button className="btn-primary" onClick={() => setBuyOpen(true)}><Plus size={18} /> {t("common.recordBuy")}</button>} />
      ) : (
        <Panel className="card overflow-hidden" testid="holdings-table">
          <div className="overflow-x-auto">
          <div className="min-w-[900px]">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-100 label">
            <div className="col-span-4">{t("holdings.instrument")}</div>
            <div className="col-span-2 text-right">{t("holdings.quantity")}</div>
            <div className="col-span-2 text-right">{t("holdings.priceUsd")}</div>
            <div className="col-span-2 text-right">{t("holdings.valueUsd")}</div>
            <div className="col-span-2 text-right">{t("holdings.pl")}</div>
          </div>
          {holdings.map((h) => (
            <div key={h.ticker} className="border-b border-slate-50 last:border-0">
              <div
                data-testid={`holding-row-${h.ticker}`}
                className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-slate-50/60 transition-colors cursor-pointer"
                onClick={() => setExpanded(expanded === h.ticker ? null : h.ticker)}
              >
                <div className="col-span-4 flex items-center gap-3">
                  <ChevronDown size={16} className={`text-slate-400 transition-transform ${expanded === h.ticker ? "rotate-180" : ""}`} />
                  <InstrumentLogo logo={h.logo} ticker={h.ticker} size={36} />
                  <div>
                    <Link to={`/instrument/${h.ticker}`} onClick={(e) => e.stopPropagation()} className="font-medium text-slate-900 hover:text-brand-600">{h.ticker}</Link>
                    <div className="text-xs text-slate-400 truncate max-w-[180px]">{h.name}</div>
                  </div>
                </div>
                <div className="col-span-2 text-right tabular">{num(h.quantity, 4, { crypto: isCryptoType(h.asset_type) })}</div>
                <div className="col-span-2 text-right tabular">
                  {display(h.current_price_usd, { crypto: isCryptoType(h.asset_type) }).primary}
                  <div className={`text-xs ${gainColor(h.change_pct)}`}>{pct(h.change_pct)}</div>
                </div>
                <div className="col-span-2 text-right tabular font-medium">
                  {display(h.market_value_usd, { crypto: isCryptoType(h.asset_type) }).primary}
                  {display(h.market_value_usd, { crypto: isCryptoType(h.asset_type) }).secondary && <div className="text-xs font-normal text-slate-400">≈ {display(h.market_value_usd, { crypto: isCryptoType(h.asset_type) }).secondary}</div>}
                </div>
                <div className="col-span-2 flex flex-col items-end gap-1">
                  <GainBadge value={h.gain_pct} testid={`holding-gain-${h.ticker}`} />
                  <span className={`text-xs tabular ${gainColor(h.gain_usd)}`}>{display(h.gain_usd, { crypto: isCryptoType(h.asset_type) }).primary}</span>
                  {display(h.gain_usd, { crypto: isCryptoType(h.asset_type) }).secondary && <span className="text-xs tabular text-slate-400">≈ {display(h.gain_usd, { crypto: isCryptoType(h.asset_type) }).secondary}</span>}
                </div>
              </div>
              <AnimatePresence>
                {expanded === h.ticker && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }} className="overflow-hidden bg-slate-50/50"
                  >
                    <div className="px-6 py-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="label">{t("holdings.buyLots", { count: h.lots.length })}</span>
                        <button data-testid={`sell-${h.ticker}-btn`} className="btn-outline py-2 text-sm" onClick={() => setSell({ open: true, ticker: h.ticker })}>
                          <ArrowDownLeft size={15} /> {t("common.sell")}
                        </button>
                      </div>
                      <div className="space-y-2">
                        {h.lots.map((l) => (
                          <div key={l.id} data-testid={`lot-${l.id}`} className={`grid ${LOT_GRID} gap-3 items-center bg-white rounded-xl px-4 py-3 border border-slate-100 text-sm`}>
                            <div>
                              <div className="text-slate-500 text-xs">{t("holdings.bought")}</div>
                              <div className="font-medium">{shortDate(l.purchase_date)}</div>
                            </div>
                            <div>
                              <div className="text-slate-500 text-xs">{t("common.broker")}</div>
                              <div className="font-medium">{l.broker || "—"}</div>
                            </div>
                            <div className="tabular text-right">
                              <div className="text-slate-500 text-xs">{t("holdings.quantity")}</div>
                              <div>{num(l.quantity, 4, { crypto: isCryptoType(h.asset_type) })}</div>
                            </div>
                            <div className="tabular text-right">
                              <div className="text-slate-500 text-xs">{t("holdings.price")}</div>
                              <div>{money(l.purchase_price, l.currency, { crypto: isCryptoType(h.asset_type) })}</div>
                            </div>
                            <div className="tabular text-right">
                              <div className="text-slate-500 text-xs">{t("holdings.fee")}</div>
                              <div className="text-slate-500">{l.fee > 0 ? money(l.fee, l.currency) : "—"}</div>
                            </div>
                            <div className="tabular text-right">
                              <div className="text-slate-500 text-xs">{t("holdings.value")}</div>
                              <div>{display(l.current_value_usd, { crypto: isCryptoType(h.asset_type) }).primary}</div>
                              {display(l.current_value_usd, { crypto: isCryptoType(h.asset_type) }).secondary && <div className="text-xs text-slate-400">≈ {display(l.current_value_usd, { crypto: isCryptoType(h.asset_type) }).secondary}</div>}
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <GainBadge value={l.gain_pct} />
                              <span className={`text-xs tabular ${gainColor(l.gain_usd)}`}>{display(l.gain_usd, { crypto: isCryptoType(h.asset_type) }).primary}</span>
                              {display(l.gain_usd, { crypto: isCryptoType(h.asset_type) }).secondary && <span className="text-xs tabular text-slate-400">≈ {display(l.gain_usd, { crypto: isCryptoType(h.asset_type) }).secondary}</span>}
                            </div>
                            {l.notes && <div className="col-span-full text-xs text-slate-500 border-t border-slate-50 pt-2 mt-1">📝 {l.notes}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
          </div>
          </div>
        </Panel>
      )}

      {policies.length > 0 && (
        <Panel className="card p-6" testid="holdings-insurance-summary">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-brand-500" />
              <h3 className="font-heading font-semibold tracking-tight text-lg">{t("insurance.title")}</h3>
              <span className="pill bg-slate-100 text-slate-500">{policies.length}</span>
            </div>
            <Link to="/insurance" className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1">
              {t("common.viewAll")} <ArrowUpRight size={15} />
            </Link>
          </div>
          <div className="mt-4 flex flex-wrap gap-6">
            {Object.entries(policyTotalsByCcy).map(([ccy, total]) => (
              <div key={ccy}>
                <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{t("insurance.currentValue")} ({ccy})</span>
                <div className="mt-1 text-2xl font-heading font-semibold tracking-tight tabular">{money(total, ccy)}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <TaxImpactPanel />
      <TransactionsPanel />

      <BuyDialog open={buyOpen} onClose={() => setBuyOpen(false)} onDone={load} />
      <SellDialog open={sell.open} ticker={sell.ticker} onClose={() => setSell({ open: false, ticker: "" })} onDone={load} />
    </div>
  );
}
